/**
 * Mock MultiplayerApi for offline testing.
 *
 * When `__RUNDOT_MULTIPLAYER_DEV_SERVER__` is set (injected by the Vite
 * multiplayer plugin during `vite dev`), delegates to a real WsMultiplayerApi
 * connected to the local dev server. Otherwise creates fake in-memory rooms.
 */

import type { MatchmakeOptions, MultiplayerApi } from './MultiplayerApi'
import { WsMultiplayerApi } from './WsMultiplayerApi'
import { RundotServerRoom } from './RundotServerRoom'
import type { ServerRoom } from './ServerRoom'
import type { Protocol, JoinTicketRequest, ListUserRoomsOptions, RealtimeRoomSummary, RoomOptions } from '../shared'
import { ServerWebSocket } from './ServerWebSocket'
import { validateRoomOptions } from './validateRoomOptions'
import type { Profile } from '../../rundot-game-api/types'

interface DevTicketRequest extends JoinTicketRequest {
  gameId: string
  profileId?: string
  username?: string
  avatarUrl?: string | null
}

const FAKE_PROFILE_SESSION_KEY = '__rundot_fake_tab_profile__'

interface FakeTabProfile {
  id: string
  username: string
  avatarUrl: null
}

function getOrCreateFakeTabProfile(): FakeTabProfile {
  try {
    // @rundot-ignore-storage
    const stored = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(FAKE_PROFILE_SESSION_KEY) : null
    if (stored) {
      return JSON.parse(stored) as FakeTabProfile
    }
  } catch {
    // fall through to create a new one
  }
  const suffix = Math.random().toString(16).slice(2, 6)
  const profile: FakeTabProfile = {
    id: `dev-tab-${suffix}`,
    username: `Dev Player ${suffix.toUpperCase()}`,
    avatarUrl: null,
  }
  try {
    // @rundot-ignore-storage
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(FAKE_PROFILE_SESSION_KEY, JSON.stringify(profile))
    }
  } catch {
    // ignore in sandboxed environment
  }
  return profile
}

function isPlaygroundActive(): boolean {
  return typeof window !== 'undefined' && !!(window as any).__RUNDOT_GAME_PLAYGROUND__?.enabled
}

function readDevServerUrl(): string | undefined {
  return typeof window !== 'undefined'
    ? (window as any).__RUNDOT_MULTIPLAYER_DEV_SERVER__
    : undefined
}

function resolveDevIdentity(getProfile?: () => Profile | undefined): {
  profileId?: string
  username?: string
  avatarUrl?: string | null
} {
  if (!isPlaygroundActive()) {
    const fakeProfile = getOrCreateFakeTabProfile()
    return { profileId: fakeProfile.id, username: fakeProfile.username, avatarUrl: fakeProfile.avatarUrl }
  }
  const profile = getProfile?.()
  return { profileId: profile?.id, username: profile?.username, avatarUrl: profile?.avatarUrl }
}

function createDevDelegate(
  serverUrl: string,
  getProfile?: () => Profile | undefined,
): WsMultiplayerApi {
  return new WsMultiplayerApi({
    serverUrl,
    getJoinTicket: async (req) => {
      const { profileId, username, avatarUrl } = resolveDevIdentity(getProfile)
      const body: DevTicketRequest = {
        ...req,
        gameId: 'dev-game',
        profileId,
        username,
        avatarUrl,
      }
      const resp = await fetch(`${serverUrl}/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: 'Ticket request failed' }))
        throw new Error(err.error || 'Ticket request failed')
      }
      const data = await resp.json()
      return data.ticketId
    },
    listUserRooms: async (_options) => {
      const { profileId } = resolveDevIdentity(getProfile)
      const url = profileId
        ? `${serverUrl}/rooms?profileId=${encodeURIComponent(profileId)}`
        : `${serverUrl}/rooms`
      const resp = await fetch(url)
      if (!resp.ok) throw new Error('Failed to list rooms')
      const data = await resp.json()
      return data.rooms as RealtimeRoomSummary[]
    },
  })
}

export interface MockMultiplayerApiOptions {
  getProfile?: () => Profile | undefined
}

export class MockMultiplayerApi implements MultiplayerApi {
  private readonly delegate: MultiplayerApi | null = null
  /** Rooms created through the offline mock, returned by getUserRooms. */
  private readonly mockRooms: RealtimeRoomSummary[] = []

  constructor(options?: MockMultiplayerApiOptions) {
    const devServerUrl = readDevServerUrl()

    if (devServerUrl) {
      this.delegate = createDevDelegate(devServerUrl, options?.getProfile)
    }
  }

  async createRoom<P extends Protocol>(roomType: string, opts?: RoomOptions): Promise<ServerRoom<P>> {
    // Validate before both the dev-server and offline paths so a creator trips on
    // the same rule the ticket route enforces in prod. joinOrCreate/matchmake
    // funnel here on the offline path; the dev-server path re-validates server-side.
    validateRoomOptions(opts)
    if (this.delegate) return this.delegate.createRoom<P>(roomType, opts)

    console.warn(
      '[RUN] Multiplayer running in offline mock mode — rooms will not connect.\n' +
      'To enable local multiplayer, add rundotMultiplayerPlugin() to your Vite config.'
    )

    const ws = new ServerWebSocket({
      url: 'ws://localhost:0/mock',
      maxReconnectAttempts: 0,
    })

    const roomCode = Math.random().toString(36).slice(2, 8).toUpperCase()
    const playerId = `mock-player-${Math.random().toString(36).slice(2, 8)}`
    const maxPlayers = opts?.createOptions?.maxPlayers ?? 10

    const room = new RundotServerRoom<P>({
      type: 'room:joined',
      roomCode,
      playerId,
      _roomId: roomCode,
      maxPlayers,
      isCreator: true,
    }, ws)

    const now = Date.now()
    this.mockRooms.push({
      roomId: `mock-${roomCode}`,
      roomCode,
      roomType,
      appId: 'mock-app',
      players: [playerId],
      maxPlayers,
      isPrivate: false,
      status: 'active',
      createdAt: now,
      updatedAt: now,
    })

    return room
  }

  async joinRoomByCode<P extends Protocol>(code: string): Promise<ServerRoom<P>> {
    if (this.delegate) return this.delegate.joinRoomByCode<P>(code)
    return this.createRoom<P>(`mock-join-${code}`)
  }

  async joinOrCreateRoom<P extends Protocol>(roomType: string, opts?: RoomOptions): Promise<ServerRoom<P>> {
    if (this.delegate) return this.delegate.joinOrCreateRoom<P>(roomType, opts)
    return this.createRoom<P>(roomType, opts)
  }

  async matchmakeRoom<P extends Protocol>(roomType: string, opts?: MatchmakeOptions): Promise<ServerRoom<P>> {
    if (this.delegate) return this.delegate.matchmakeRoom<P>(roomType, opts)
    // Offline: no pool/opponent, so a match just yields a solo room.
    return this.createRoom<P>(roomType, opts)
  }

  async getUserRooms(options?: ListUserRoomsOptions): Promise<RealtimeRoomSummary[]> {
    if (this.delegate) return this.delegate.getUserRooms(options)
    let rooms = this.mockRooms
    if (!options?.includeDisposed) rooms = rooms.filter((r) => r.status === 'active')
    if (options?.appId) rooms = rooms.filter((r) => r.appId === options.appId)
    return [...rooms]
  }
}
